/**
 * Cloudflare Turnstile widget loader + React hook.
 *
 * Loads the official Turnstile script once per page, renders an
 * invisible-by-default widget into a target element, and surfaces the
 * resulting token to the caller via a callback.
 *
 * Why script-tag rather than the `@marsidev/react-turnstile` package:
 *   - One fewer dep to upgrade + audit.
 *   - The official script is the upstream Cloudflare recommends.
 *   - Bundle size: zero.
 *
 * If `VITE_TURNSTILE_SITE_KEY` is empty, `useTurnstile()` returns
 * `enabled: false` and the consuming form treats the token as `null`.
 * The backend mirrors this — `DOVACH_REQUIRE_TURNSTILE=false` (the
 * default when no secret is set) accepts requests without a token. So
 * a misconfigured frontend is a soft-fail: signups still work, just
 * without bot-mitigation. Configure both halves to turn it on.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileObject {
  render: (
    container: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
      size?: "normal" | "flexible" | "compact";
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileObject;
  }
}

const SITE_KEY: string =
  (import.meta as { env?: { VITE_TURNSTILE_SITE_KEY?: string } }).env
    ?.VITE_TURNSTILE_SITE_KEY ?? "";

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      // Already loading; wait for the global to appear.
      const start = Date.now();
      const poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          resolve();
        } else if (Date.now() - start > 10000) {
          clearInterval(poll);
          reject(new Error("turnstile_script_timeout"));
        }
      }, 50);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile_script_load_failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Hook that mounts a Turnstile widget into the returned ref. Token is
 * `null` until the widget verifies; once verified, it's the string to
 * send to the backend. `enabled` is false if no site key was provided
 * at build time — the consuming form should hide the widget and pass
 * `null` to api.signup/forgotPassword (backend will skip the check).
 */
export function useTurnstile(): {
  enabled: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  token: string | null;
  reset: () => void;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const enabled = SITE_KEY.length > 0;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: "light",
          callback: (t) => setToken(t),
          "expired-callback": () => setToken(null),
          "error-callback": () => setToken(null),
        });
      })
      .catch(() => {
        // Script load failure: render fallback. The form will see
        // token=null forever; if the backend requires Turnstile, the
        // submit will 403 and the user retries.
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already torn down */
        }
        widgetIdRef.current = null;
      }
    };
  }, [enabled]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* widget gone */
      }
    }
  }, []);

  return { enabled, containerRef, token, reset };
}
